import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, errorMessage, fieldErrors } from '@/lib/api'
import type { InvoiceSeller } from '@/types'
import Button from '@/components/ui/Button'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import LogoLoader from '@/components/ui/LogoLoader'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'

type Editable = InvoiceSeller

const fields: { key: keyof Editable; label: string; placeholder?: string }[] = [
  { key: 'business_name', label: 'Business name', placeholder: 'MPG Printing Sdn Bhd' },
  { key: 'registration_no', label: 'Registration no. (SSM)', placeholder: '202601012345 (1234567-A)' },
  { key: 'sst_no', label: 'SST no. (leave blank if not registered)' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
]

const bankFields: { key: keyof Editable; label: string }[] = [
  { key: 'bank_name', label: 'Bank' },
  { key: 'bank_account_name', label: 'Account name' },
  { key: 'bank_account_no', label: 'Account no.' },
]

/**
 * The seller details printed on every invoice. Phone and address are the
 * invoice's own, separate from the pickup point on Settings > Delivery,
 * because the billing address may not be where parcels are collected.
 */
export default function AdminInvoiceSettings() {
  const settings = useQuery({
    queryKey: ['admin-invoice-settings'],
    queryFn: async () => (await api.get<InvoiceSeller>('/admin/invoice-settings')).data,
  })

  if (settings.isLoading || !settings.data) return <LogoLoader />

  return <InvoiceForm initial={settings.data} />
}

function InvoiceForm({ initial }: { initial: InvoiceSeller }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<Editable>(() => ({
    business_name: initial.business_name ?? '',
    registration_no: initial.registration_no ?? '',
    sst_no: initial.sst_no ?? '',
    email: initial.email ?? '',
    phone: initial.phone ?? '',
    address: initial.address ?? '',
    bank_name: initial.bank_name ?? '',
    bank_account_name: initial.bank_account_name ?? '',
    bank_account_no: initial.bank_account_no ?? '',
    note: initial.note ?? '',
  }))
  const [errors, setErrors] = useState<Record<string, string[]>>({})

  const save = useMutation({
    mutationFn: async () => {
      // Blank means "not set", so it is sent as null rather than ''.
      const body = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value?.trim() || null]))
      return (await api.patch<InvoiceSeller & { message: string }>('/admin/invoice-settings', body)).data
    },
    onSuccess: (res) => {
      setErrors({})
      queryClient.setQueryData(['admin-invoice-settings'], res)
      queryClient.invalidateQueries({ queryKey: ['invoice'] })
      toast.success(res.message)
    },
    onError: (err) => {
      setErrors(fieldErrors(err))
      toast.error('Save failed', errorMessage(err))
    },
  })

  const set = (key: keyof Editable) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }))

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <PageHeader title="Invoice" subtitle="What every invoice says about MPG. Admin, sales and the customer print it from the order page." />

      <Card className="mb-5">
        <SectionLabel>Business</SectionLabel>
        <div className="grid gap-4 sm:grid-cols-2">
          {fields.map(({ key, label, placeholder }) => (
            <Field key={key} label={label} htmlFor={`inv-${key}`} required={key === 'business_name'} error={errors[key]?.[0]}>
              <Input id={`inv-${key}`} value={form[key] ?? ''} onChange={set(key)} placeholder={placeholder} />
            </Field>
          ))}
        </div>
        <Field label="Address" htmlFor="inv-address" className="mt-4" error={errors.address?.[0]}>
          <Textarea id="inv-address" value={form.address ?? ''} onChange={set('address')} placeholder={'Lot 123, Jalan Besar\n34300 Bagan Serai, Perak'} />
        </Field>
      </Card>

      <Card className="mb-5">
        <SectionLabel>Payment details</SectionLabel>
        <p className="mb-4 text-sm text-foreground/60">Printed as "Pay to" on an invoice that is not paid yet, such as an order on credit.</p>
        <div className="grid gap-4 sm:grid-cols-3">
          {bankFields.map(({ key, label }) => (
            <Field key={key} label={label} htmlFor={`inv-${key}`} error={errors[key]?.[0]}>
              <Input id={`inv-${key}`} value={form[key] ?? ''} onChange={set(key)} />
            </Field>
          ))}
        </div>
      </Card>

      <Card className="mb-5">
        <SectionLabel>Note</SectionLabel>
        <Field label="Printed at the bottom of every invoice (optional)" htmlFor="inv-note" error={errors.note?.[0]}>
          <Textarea id="inv-note" value={form.note ?? ''} onChange={set('note')} placeholder="Thank you for your business." />
        </Field>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          Save
        </Button>
      </div>
    </form>
  )
}
